// vue
import { ref } from '@vue/composition-api';

const getParentCheckState = (math_and: boolean, math_or: boolean) => {
    /*
        Example:
        checked values | (AND) | (OR)  | check type |
        ---------------|-------|-------|------------|
        0101010        | false |  true |   partial  |
        0000000        | false | false | unchecked  |
        1111111        |  true |  true |   checked  |
    */
    if (!math_and && math_or) {
        return { 'partialChecked': true };
    }
    else if (math_and && math_or) {
        return { 'checked': true };
    }
    else {
        return { 'checked': false };
    }
}
    

export const useLayerTreeOption = () => {
    // refs
    const mapboxLayerState = ref();

    // methods
    const genTreeKeyMap = (nodes: any, keyMap: {}, keyString: string) => {
        nodes.forEach((node: any) => {
            if (node.children && node.children.length > 0) {
                genTreeKeyMap(node.children, keyMap, keyString);
            }
            keyMap[node[keyString]] = {
                checked: node.isVisible,
            };
        });
        return keyMap;
    };

    const genTreeKeyMapCourseSetupNodes = (nodes: any, keyMap: {}, keyString: string, parentNode: {math_and: boolean, math_or: boolean} = {math_and: true, math_or: false}) => {
        nodes.forEach((node: any) => {
            const parentNode2 = {
                parent: node[keyString],
                math_and: true,
                math_or: false
            }
            if (node.children && node.children.length > 0) {

                genTreeKeyMapCourseSetupNodes(node.children, keyMap, keyString, parentNode2);
                //-------Updating current non leaf node's state-----------------------------------------------
                keyMap[parentNode2.parent] = getParentCheckState(parentNode2.math_and, parentNode2.math_or)
                //-------Updating a level above non leaf node's state-----------------------------------------
                parentNode.math_and = parentNode2.math_and && parentNode.math_and;
                parentNode.math_or = parentNode2.math_or || parentNode.math_or;
                //--------------------------------------------------------------------------------------------                
            }
            if(!keyMap[node[keyString]]){
                const isChecked = node.isVisible;
                keyMap[node[keyString]] = {checked: isChecked};
                        
                parentNode.math_and = isChecked && parentNode.math_and;
                parentNode.math_or = isChecked || parentNode.math_or;
                
                keyMap[node[keyString]] = {
                    checked: node.isVisible,
                };

            }
        });
        return keyMap;
    };

    const genTreeKeyMapGeoRecordTree = (
        nodes: any,
        keyMap: {},
        keyString: string,
        userLayerSelectionData: any,
        parentNode: {math_and: boolean, math_or: boolean} = {math_and: true, math_or: false}
    ) => {
        nodes.forEach((node: any) => {
            const parentNode2 = {
                parent: node[keyString],
                math_and: true,
                math_or: false
            }

            if (node.children && node.children.length > 0) { //Non-leaf nodes
                genTreeKeyMapGeoRecordTree(
                    node.children,
                    keyMap,
                    keyString,
                    userLayerSelectionData,
                    parentNode2
                );
            
                //-------Updating current non leaf node's state-----------------------------------------------
                keyMap[parentNode2.parent] = getParentCheckState(parentNode2.math_and, parentNode2.math_or)
                //-------Updating a level above non leaf node's state-----------------------------------------
                parentNode.math_and = parentNode2.math_and && parentNode.math_and;
                parentNode.math_or = parentNode2.math_or || parentNode.math_or;
                //--------------------------------------------------------------------------------------------
            }
            if(!keyMap[node[keyString]]){

                const isChecked = userLayerSelectionData.id !== 0 ? userLayerSelectionData.visibleRecords.includes( node.id,): true;
                keyMap[node[keyString]] = {checked: isChecked};
                        
                parentNode.math_and = isChecked && parentNode.math_and;
                parentNode.math_or = isChecked || parentNode.math_or;
                
            }
                
        });
        return keyMap;
    };

    const generateMapboxLayerState = (
        nodes: any,
        map: any,
        keyValue: any,
        isGeoRecord: any,
    ) => {
        nodes.forEach((node) => {
            if (node.children.length > 0) {
                generateMapboxLayerState(
                    node.children,
                    map,
                    keyValue,
                    isGeoRecord,
                );
            } else {
                map[node[keyValue]] = {
                    isVisible:
                        node.key === 'sat' || node.key === 'mosaic'
                            ? false
                            : node.isVisible,
                    isGeoRecord: isGeoRecord,
                    children:
                        node.style && node.style.metadata
                            ? node.style.metadata.children
                            : null,
                };
            }
        });

        return map;
    };

    const generateGeoRecordLayerState = (
        nodes: any,
        map: any,
        keyValue: any,
        isGeoRecord: any,
        userLayerSelectionData: any,
    ) => {
        nodes.forEach((node) => {
            if (node.children.length > 0) {
                generateGeoRecordLayerState(
                    node.children,
                    map,
                    keyValue,
                    isGeoRecord,
                    userLayerSelectionData,
                );
            } else {
                map[node[keyValue]] = {
                    isVisible:
                        userLayerSelectionData.id !== 0
                            ? userLayerSelectionData.visibleRecords.includes(
                                  node.id,
                              )
                            : true,
                    isGeoRecord: isGeoRecord,
                    children:
                        node.style && node.style.metadata
                            ? node.style.metadata.children
                            : null,
                };
            }
        });

        return map;
    };

    const updatePermanentLayers = (userLayerSelectionData:any, mapboxLayerTree:any) => {
        if((userLayerSelectionData||{}).permanentLayers == null){       //permanentLayers will be null if this course doesn't have any data saved before in render_selection. It will be an empty array if data is saved but no permanent layers have been selected.
            return;
        }
        if(mapboxLayerTree && mapboxLayerTree.length && mapboxLayerTree[0].children){
            const permanentLayers = ((userLayerSelectionData||{}).permanentLayers)||[];
            const childrenUpdated = (mapboxLayerTree[0].children||[]).map(obj=>{
                obj.isVisible = permanentLayers.includes(obj.key)
                return obj;
            })
            mapboxLayerTree[0].children = childrenUpdated
        }
    }

    const refreshLayerTreeState = (
        geoRecordTree: any,
        mapboxLayerTree: any,
        userLayerSelectionData: any,
    ) => {
        const map = {};
        generateGeoRecordLayerState(
            geoRecordTree,
            map,
            'id',
            true,
            userLayerSelectionData,
        );
        updatePermanentLayers(userLayerSelectionData, mapboxLayerTree)
        generateMapboxLayerState(mapboxLayerTree, map, 'data', false);

        map['measure-records'] = {
            isVisible: true,
            isGeoRecord: false,
        };

        map['3d-terrain'] = {
            isVisible: true,
            isGeoRecord: false,
        };

        map['hillshade'] = {
            isVisible: false,
            isGeoRecord: false,
        };

        map['airtable-records'] = {
            isVisible: true,
            isGeoRecord: false,
        };

        map['airtable-buffer-layer'] = {
            isVisible: true,
            isGeoRecord: false,
        };

        map['ball-position'] = {
            isVisible: true,
            isGeoRecord: false,
        };

        mapboxLayerState.value = map;

        const mapBoxLayerStateKeys = Object.keys(mapboxLayerState.value);
        const mapKeys = Object.keys(map);

        let difference = mapBoxLayerStateKeys
            .filter((x) => !mapKeys.includes(x))
            .concat(mapKeys.filter((x) => !mapBoxLayerStateKeys.includes(x)));

        if (Array.isArray(difference) && difference.length > 0) {
            for (let i = 0; i < difference.length; i++) {
                mapboxLayerState.value[difference[i]] = map[difference[i]];
            }
        }

        return mapboxLayerState.value;
    };

    const addFogLayerOnMap = (map: any): void => {
        map.setFog({
            color: 'rgb(186, 210, 235)', // Lower atmosphere
            'high-color': 'rgb(36, 92, 223)', // Upper atmosphere
            'horizon-blend': 0.01, // Atmosphere thickness (default 0.2 at low zooms)
            'space-color': 'rgb(11, 11, 25)', // Background color
            'star-intensity': 0.4, // Background star brightness (default 0.35 at low zoooms )
        });
    };

    return {
        genTreeKeyMap,
        addFogLayerOnMap,
        refreshLayerTreeState,
        genTreeKeyMapGeoRecordTree,
        genTreeKeyMapCourseSetupNodes
    };
};
